import React, { FocusEvent, KeyboardEvent as ReactKeyboardEvent, useEffect, useRef, useState } from 'react'
import { sanitizeCell } from '../Utilities/tools'
import './SpellcheckedEditor.css'
import { useSpellcheck } from '../hooks/useSpellcheck'
import type { SpellcheckWord } from '../Types/SpellcheckTypes'
import SpellcheckMenu from './SpellcheckMenu'

interface SpellcheckEditorProps {
  value: string;
  onChange: (newValue: string) => void;
  language: string;
  onFocus?: () => void;
  onBlur?: (e: FocusEvent<HTMLDivElement>) => void;
  onKeyDown?: (e: ReactKeyboardEvent<HTMLDivElement>) => void;
  onInput?: (newValue: string) => void;
  readOnly?: boolean;
  className?: string;
}

const SpellcheckedEditor: React.FC<SpellcheckEditorProps> = ({
                                                               value,
                                                               onChange,
                                                               language,
                                                               onFocus,
                                                               onBlur,
                                                               onKeyDown,
                                                               onInput,
                                                               readOnly = false,
                                                               className = '',
                                                             }) => {
  const editorRef = useRef<HTMLDivElement>(null)
  const { checkSpelling } = useSpellcheck()
  const [selectedMisspelledWord, setSelectedMisspelledWord] = useState<SpellcheckWord | null>(null)
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 })
  const spellcheckCounterRef = useRef(0) // Track spellcheck requests

  // Update editor content when value changes
  useEffect(() => {
    if (editorRef.current && document.activeElement !== editorRef.current) {
      editorRef.current.innerHTML = sanitizeCell(value)
    }
  }, [value])

  // Handle clicking on a misspelled word
  const handleMisspelledWordClick = (e: MouseEvent, word: SpellcheckWord) => {
    e.preventDefault()
    e.stopPropagation()

    const target = e.currentTarget as HTMLElement
    const rect = target.getBoundingClientRect()

    setMenuPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    })
    setSelectedMisspelledWord(word)
  }

  // Handle selecting a suggestion from the menu
  const handleSelectSuggestion = (suggestion: string) => {
    if (!selectedMisspelledWord || !editorRef.current) return

    const plainText = editorRef.current.textContent || ''
    const beforeWord = plainText.substring(0, selectedMisspelledWord.start)
    const afterWord = plainText.substring(selectedMisspelledWord.end)
    const updatedText = beforeWord + suggestion + afterWord

    // Update content and trigger change
    editorRef.current.textContent = updatedText
    onChange(updatedText)
    setSelectedMisspelledWord(null)

    // Clear existing misspelled word spans before re-running spellcheck
    clearMisspelledWords()

    // Re-run spellcheck immediately without delay
    performSpellcheck(updatedText)
  }

  // Clear all misspelled word spans
  const clearMisspelledWords = () => {
    if (!editorRef.current) return

    const spans = editorRef.current.querySelectorAll('.misspelled-word')
    spans.forEach(span => {
      const textNode = document.createTextNode(span.textContent || '')
      span.parentNode?.replaceChild(textNode, span)
    })

    // Normalize text nodes to merge adjacent ones
    editorRef.current.normalize()
  }

  // Handle blur events
  const handleBlur = (e: FocusEvent<HTMLDivElement>) => {
    const newValue = sanitizeCell(e.target.innerHTML)

    if (newValue !== value) {
      onChange(newValue)
    }

    if (newValue.trim()) {
      performSpellcheck(newValue)
    }

    onBlur?.(e)
  }

  const handleFocus = () => {
    onFocus?.()
  }

  // Perform spellchecking and mark misspelled words
  const performSpellcheck = async (content: string) => {
    if (!content || readOnly || !editorRef.current) return

    // Increment counter and capture current request ID
    const requestId = ++spellcheckCounterRef.current

    try {
      const result = await checkSpelling(content, language)

      // Check if this is still the latest request
      if (requestId !== spellcheckCounterRef.current) {
        // This request is outdated, ignore it
        return
      }

      const misspelledWords = result?.words?.filter(w => !w.correct && w.should_check) || []

      // Clear existing spans before marking new ones
      clearMisspelledWords()

      if (misspelledWords.length > 0) {
        markMisspelledWords(editorRef.current, misspelledWords)
      }
    } catch (error) {
      // Only log error if this was the latest request
      if (requestId === spellcheckCounterRef.current) {
        console.error('Spellcheck error:', error)
      }
    }
  }

  // Mark misspelled words in the editor
  const markMisspelledWords = (editorElement: HTMLElement, misspelledWords: SpellcheckWord[]) => {
    const walker = document.createTreeWalker(
      editorElement,
      NodeFilter.SHOW_TEXT,
      null,
    )

    const textNodes: Node[] = []
    let node
    while ((node = walker.nextNode())) {
      textNodes.push(node)
    }

    // Process misspelled words in reverse order to maintain correct positions
    for (const word of misspelledWords.reverse()) {
      let currentPos = 0

      for (const textNode of textNodes) {
        const textContent = textNode.textContent || ''
        const nodeEnd = currentPos + textContent.length

        if (word.start >= currentPos && word.start < nodeEnd) {
          const nodeStartPos = word.start - currentPos
          const nodeEndPos = Math.min(word.end - currentPos, textContent.length)

          // Create misspelled word span
          const span = document.createElement('span')
          span.textContent = textContent.substring(nodeStartPos, nodeEndPos)
          span.className = 'misspelled-word'
          span.onclick = (e) => handleMisspelledWordClick(e, word)

          // Replace text with span
          const beforeText = textContent.substring(0, nodeStartPos)
          const afterText = textContent.substring(nodeEndPos)

          if (beforeText) {
            textNode.textContent = beforeText
            textNode.parentNode?.insertBefore(span, textNode.nextSibling)
            if (afterText) {
              textNode.parentNode?.insertBefore(document.createTextNode(afterText), span.nextSibling)
            }
          } else {
            textNode.parentNode?.insertBefore(span, textNode)
            textNode.textContent = afterText
          }
          break
        }
        currentPos = nodeEnd
      }
    }
  }

  // Handle input events
  const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
    const newValue = sanitizeCell(e.currentTarget.innerHTML)
    onInput?.(newValue)
  }

  return (
    <div className="spellcheck-editor-container">
      <div
        ref={editorRef}
        className={`spellcheck-editor ${className}`}
        contentEditable={!readOnly}
        suppressContentEditableWarning
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={onKeyDown}
        onInput={handleInput}
        dangerouslySetInnerHTML={{ __html: sanitizeCell(value) || '' }}
      />
      {selectedMisspelledWord && (
        <SpellcheckMenu
          word={selectedMisspelledWord}
          position={menuPosition}
          onSelectSuggestion={handleSelectSuggestion}
          onClose={() => setSelectedMisspelledWord(null)}
        />
      )}
    </div>
  )
}

export default SpellcheckedEditor
